import React, { useMemo, useState } from 'react';
import { useQuery } from '@apollo/client';
import { createUseStyles } from 'react-jss';
import type { History } from 'history';
import { Theme, Search, ColorBadge, SelectOption, Select } from '@spoiler-alert/ui-library';
import { AdminUnassignedPO, AdminUnassignedPOQuery, PO_STATUS } from './po-queries';
import useStickyState from '../../hooks/use-sticky-state';
import { VColumn, VRowAction, VTable } from '../../components/v-table';
import routePaths from '../../route-paths';
import type { VTableType } from '../../components/v-table/v-table-types';
import moment from 'moment';
import accounting from 'accounting';
import { translateStatusLabel } from './po-status';

const useStyles = createUseStyles({
  wrap: {
    position: 'relative',
    border: `1px solid ${Theme.tableBorderColor}`,
  },
  header: {
    padding: 10,
    display: 'flex',
    gap: 20,
    borderBottom: `1px solid ${Theme.grey10}`,
    alignItems: 'center',
  },
  negative: {
    color: Theme.red,
  },
});

interface SelectItem {
  text: string;
  value: string;
}

const GroupedPOStatus = {
  'NOT SUBMITTED': 'Not Assigned',
  OVERDUE: 'Overdue',
  ASSIGNED: 'PO Assigned',
  CANCELLED: 'Cancelled',
};

const getThemeByStatus = (status: string) => {
  if (status === 'NOT SUBMITTED') return 'grey';
  if (status === 'OVERDUE') return 'red';
  if (status === 'ASSIGNED') return 'green';
  if (status === 'CANCELLED') return 'red';
  return 'grey';
};

const AwaitingPO: React.FC<{ history: History }> = ({ history }) => {
  const classes = useStyles();
  const [searchText, setSearchText] = useState<string>('');
  const [selectedSellers, setSelectedSellers] = useStickyState<SelectItem[]>([], 'po-sellers');
  const [selectedBuyers, setSelectedBuyers] = useStickyState<SelectItem[]>([], 'po-buyers');
  const [selectedStatuses, setSelectedStatuses] = useStickyState<SelectItem[]>(
    [
      { text: GroupedPOStatus.OVERDUE, value: 'OVERDUE' },
      { text: GroupedPOStatus['NOT SUBMITTED'], value: 'NOT SUBMITTED' },
    ],
    'po-group-status',
  );
  const { data, loading } = useQuery(AdminUnassignedPOQuery);

  const ordersList = data?.adminUnassignedPOQuery || [];

  const { sellerOptions, buyerOptions, statusOptions } = useMemo(() => {
    if (ordersList.length > 0) {
      const { sellerSet, buyerSet } = ordersList.reduce(
        (options, order) => {
          options.sellerSet.add(order.sellerSiteName);
          options.buyerSet.add(order.buyerSiteName);
          return options;
        },
        { sellerSet: new Set<string>(), buyerSet: new Set<string>() },
      );
      return {
        sellerOptions: [...sellerSet].map((v) => (
          <SelectOption checkbox key={v} value={v}>
            {v}
          </SelectOption>
        )),
        buyerOptions: [...buyerSet].map((v) => (
          <SelectOption checkbox key={v} value={v}>
            {v}
          </SelectOption>
        )),
        statusOptions: Object.entries(GroupedPOStatus).map(([k, v]) => (
          <SelectOption checkbox key={k} value={k}>
            {v}
          </SelectOption>
        )),
      };
    }
    return { sellerOptions: [], buyerOptions: [], statusOptions: [] };
  }, [ordersList]);

  const handleSearch = (text: string) => {
    setSearchText(text);
  };

  const handleRowClick = (row: AdminUnassignedPO) => {
    history.push(`${routePaths.purchaseOrderManager}/awaiting-po/${row._id}`);
  };

  const columns = useMemo(() => {
    return [
      new VColumn({
        field: 'sellerSiteName',
        displayName: 'Seller',
        sortable: true,
      }),
      new VColumn({
        field: 'buyerSiteName',
        displayName: 'Buyer',
        sortable: true,
      }),
      new VColumn({
        field: 'acceptanceDate',
        displayName: 'Acceptance Date',
        sortable: true,
        defaultSort: true,
        defaultSortDirection: 'desc',
        formatter: (value) => moment(value).format('MMM D, YYYY'),
      }),
      new VColumn({
        field: 'poDueDate',
        displayName: 'PO Due Date',
        sortable: true,
        formatter: (value) => moment(value).format('MMM D, YYYY'),
      }),
      new VColumn({
        field: 'status',
        displayName: 'Status',
        sortable: true,
        formatter: (value, row) => (
          <ColorBadge id={row._id} theme={getThemeByStatus(value as string)}>
            {translateStatusLabel(value as string)}
          </ColorBadge>
        ),
      }),
      new VColumn({
        field: 'availableCredit',
        displayName: 'Available Credit',
        sortable: true,
        formatter: (value, row: AdminUnassignedPO) => (
          <span className={(value as number) - row.totalTransacted > 0 ? '' : classes.negative} title="Cached value">
            {accounting.formatMoney(value)}
          </span>
        ),
      }),
      new VColumn({
        field: 'totalTransacted',
        displayName: 'Transacted Amount',
        sortable: true,
        formatter: (value) => accounting.formatMoney(value),
      }),
      new VColumn({
        field: 'trucklaneCount',
        displayName: '# of Trucklanes',
        sortable: true,
      }),
    ];
  }, [classes]);

  const filteredList = useMemo(() => {
    if (
      ordersList.length > 0 &&
      (selectedBuyers.length > 0 || selectedSellers.length > 0 || selectedStatuses.length > 0)
    ) {
      const buyerSet = new Set<string>();
      const sellerSet = new Set<string>();
      const statusSet = new Set<string>();
      selectedBuyers.forEach((b) => buyerSet.add(b.value));
      selectedSellers.forEach((s) => sellerSet.add(s.value));
      selectedStatuses.forEach((s) => statusSet.add(s.value));
      return ordersList.filter((order) => {
        let pass = true;
        if (buyerSet.size > 0) pass = buyerSet.has(order.buyerSiteName);
        if (sellerSet.size > 0) pass = pass && sellerSet.has(order.sellerSiteName);
        if (statusSet.size > 0) pass = pass && statusSet.has(order.status);
        return pass;
      });
    }
    return ordersList;
  }, [ordersList, selectedSellers, selectedBuyers, selectedStatuses]);

  const AwaitingPOTable = VTable as VTableType<AdminUnassignedPO>;

  return (
    <div className={classes.wrap}>
      <div className={classes.header}>
        <Search onChange={handleSearch} />
        <Select
          selectAll
          autoWidth
          multiple
          label="Sellers"
          selectedItems={selectedSellers}
          onChange={(selected: SelectItem[]) => setSelectedSellers([...selected])}
        >
          {sellerOptions}
        </Select>
        <Select
          selectAll
          autoWidth
          multiple
          label="Buyers"
          selectedItems={selectedBuyers}
          onChange={(selected: SelectItem[]) => setSelectedBuyers([...selected])}
        >
          {buyerOptions}
        </Select>
        <Select
          selectAll
          autoWidth
          multiple
          label="Status"
          selectedItems={selectedStatuses}
          onChange={(selected: SelectItem[]) => setSelectedStatuses([...selected])}
        >
          {statusOptions}
        </Select>
      </div>
      <AwaitingPOTable
        cols={columns}
        data={filteredList}
        loading={loading}
        searchText={searchText}
        onRowClick={handleRowClick}
        rowActionsWidth={120}
        rowActions={[
          <VRowAction key="details" label="Order Details" onClick={(row: any) => () => handleRowClick(row)} />,
        ]}
      />
    </div>
  );
};

export default AwaitingPO;
